import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { DatarouterError } from '@zg-rentals/web-base';
import { debounce } from '@zg-rentals/zdash';

import { useFormMode } from '../contexts/FormModeContext';
import { postingConstraints } from '../utils/formValidation';

import { DEFAULT_ERROR_MESSAGE } from '../constants/ErrorMessageConstants';
import TimeConstants from '../constants/TimeConstants';

import { useWizardNavigation } from '../components/CreateListingPage/WizardNavigationProvider';

import useForm from './useForm';
import { MultiEditSoftError } from './useMultiEditMutation';

import type { UseMutationResult } from '@zg-rentals/react-query5';

/**
 * Extract the TVariables type from a UseMutationResult.
 *
 * This utility type enables automatic type inference for useFieldSave,
 * ensuring buildMutationVariables returns the exact shape expected by the mutation.
 */
type ExtractMutationVariables<TMutation> =
  TMutation extends UseMutationResult<infer _TData, infer _TError, infer TVariables, infer _TContext>
    ? TVariables
    : unknown;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UseFieldSaveOptions<T, TMutation extends UseMutationResult<any, any, any, any>> = {
  fieldName: string;
  initialValue: T;
  mutation: TMutation;
  buildMutationVariables: (value: T) => ExtractMutationVariables<TMutation>;

  // For Radio/Checkbox/Select inputs that don't fire blur events
  autoSaveOnChange?: boolean;

  // Only needed when the API error fieldName differs from the form's fieldName
  // (e.g. form uses "description" but API returns errors with fieldName "fullDescription")
  apiFieldName?: string;

  // When false, validation errors are not reported to the wizard navigation context.
  // Use for conditionally-visible fields that should only block Next when shown.
  enabled?: boolean;
};

export type UseFieldSaveReturn<T> = {
  value: T;
  onChange: (value: T | null | undefined) => void;
  onBlur?: () => void;
  error: string | null | undefined;
  isPending: boolean;
};

/**
 * Hook that encapsulates field save logic based on form mode.
 *
 * In wizard mode:
 * - Returns onBlur handler that saves automatically
 *
 * In modal mode:
 * - onBlur is undefined (no auto-save)
 * - Registers save function with FormModeContext
 * - Parent modal triggers save via context.triggerAllSaves()
 *
 * Uses existing useForm hook for validation to maintain compatibility
 * with legacy postingConstraints.
 *
 * @example
 * ```tsx
 * const updateMutation = usePropertyEditMutation({
 *   mutationHook: useRentalManagerUserPropertyFloorPlanEditV2WebApiHandler_numBedrooms,
 * });
 *
 * const { value, onChange, onBlur, error } = useFieldSave({
 *   fieldName: 'bedrooms',
 *   initialValue: 2,
 *   mutation: updateMutation,
 *   buildMutationVariables: (val) => ({
 *     postData: { floorPlanId: id, numBedrooms: val },
 *   }),
 * });
 *
 * <Select value={value} onChange={onChange} onBlur={onBlur} error={error} />
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFieldSave = <T, TMutation extends UseMutationResult<any, any, any, any>>({
  fieldName,
  initialValue,
  mutation,
  buildMutationVariables,
  autoSaveOnChange = false,
  apiFieldName,
  enabled = true,
}: UseFieldSaveOptions<T, TMutation>): UseFieldSaveReturn<T> => {
  const formModeContext = useFormMode();
  const navigation = formModeContext.mode === 'wizard' ? useWizardNavigation() : null;
  const fieldId = useId();

  const { inputs, handleChange, hasFieldError, errorObj } = useForm({ [fieldName]: initialValue }, postingConstraints);

  const value = inputs[fieldName] as T;

  const validationError = (errorObj as Record<string, string | undefined>)[fieldName];
  const effectiveApiFieldName = apiFieldName ?? fieldName;
  const mutationError = mutation.error
    ? MultiEditSoftError.is(mutation.error)
      ? mutation.error.getMessageForField(effectiveApiFieldName)
      : (mutation.error as DatarouterError).errorDetails
        ? DatarouterError.getErrorDetails(mutation.error).errors.find((error) => error.name === fieldName)?.message
        : DatarouterError.getDisplayMessage(mutation.error, DEFAULT_ERROR_MESSAGE)
    : null;

  // Centralized logic for building variables with skip checks
  const buildVariablesOrNull = useCallback(
    (val: T | null | undefined) => {
      if (hasFieldError(fieldName, val as T) || val === initialValue) {
        return null;
      }
      return buildMutationVariables(val as T);
    },
    [fieldName, initialValue, hasFieldError, buildMutationVariables],
  );

  // Stable debounced save using refs to avoid recreation
  const latestRefs = useRef({ buildVariablesOrNull, mutation, value });
  latestRefs.current = { buildVariablesOrNull, mutation, value };

  const executeSave = useCallback(async () => {
    const { buildVariablesOrNull: bvon, mutation: mut, value: val } = latestRefs.current;
    const variables = bvon(val);
    if (variables) {
      await mut.mutateAsync(variables);
    }
  }, []);

  const executeSaveDebounced = useMemo(
    () =>
      debounce((newValue: T | null | undefined) => {
        const variables = latestRefs.current.buildVariablesOrNull(newValue);
        if (variables) {
          latestRefs.current.mutation.mutateAsync(variables);
        }
      }, TimeConstants.SELECTION_DEBOUNCE_TIME_MS),
    [],
  );

  // Report validation errors to navigation context in wizard mode.
  // Gated on `enabled` so conditionally-visible fields don't block Next when hidden.
  useEffect(() => {
    if (validationError && enabled) {
      navigation?.reportError();
      return () => navigation?.clearError();
    }
  }, [validationError, navigation, formModeContext.mode, enabled]);

  // In modal mode, register mutation + variables builder
  useEffect(() => {
    if (formModeContext.mode === 'modal') {
      formModeContext.registerSave(fieldId, {
        mutation,
        buildVariables: () => buildVariablesOrNull(value),
        hasError: () => !!validationError,
      });
      return () => formModeContext.unregisterSave(fieldId);
    }
  }, [formModeContext, fieldId, mutation, value, buildVariablesOrNull, validationError]);

  const onChange = (newValue: T | null | undefined) => {
    latestRefs.current.value = newValue as T;
    handleChange({ target: { name: fieldName, value: newValue as T } });

    // Auto-save for inputs without blur events (wizard mode only)
    if (autoSaveOnChange && formModeContext.mode === 'wizard') {
      executeSaveDebounced(newValue);
    }
  };

  const validateField = useCallback(() => {
    hasFieldError(fieldName, value);
  }, [fieldName, value, hasFieldError]);

  if (formModeContext.mode === 'wizard') {
    return {
      value,
      onChange,
      onBlur: autoSaveOnChange ? undefined : executeSave, // No onBlur for auto-save inputs
      error: validationError || mutationError,
      isPending: mutation.isPending,
    };
  }

  // Modal mode
  return {
    value,
    onChange,
    onBlur: validateField, // Validate on blur, save deferred until modal Save button
    error: validationError || mutationError,
    isPending: mutation.isPending,
  };
};
